import { useEffect, useRef, useState } from "react";
import { Loader2, Upload, ChevronDown, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { ALABAMA_COUNTIES } from "@/lib/alabamaCounties";
import {
  useProfileCompletion,
  TIER_SPECS,
  type ProfileRow,
} from "@/hooks/useProfileCompletion";
import CompletionBadge from "./CompletionBadge";
import { cn } from "@/lib/utils";

interface SettingsTabProps {
  userId: string;
  email: string;
  onProfileUpdated?: () => void;
}

const ROLE_OPTIONS = ["Advocate", "Agency", "Policymaker", "Funder"];

const STAKEHOLDER_TYPES = [
  "Advocate",
  "Agency",
  "Policymaker",
  "Funder",
  "Small business / DBE",
];

const FOCUS_AREAS = [
  "Lobbying",
  "Strategic Communication",
  "Advocacy",
  "Research",
  "Economic Development",
  "Health & Wellness",
];

const LOOKING_FOR = ["Funding", "Partners", "Talent", "Mentorship", "Contracts"];

function ChipMulti({
  options,
  value,
  onChange,
  placeholder = "Select…",
}: {
  options: readonly string[];
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
}) {
  const toggle = (opt: string) => {
    onChange(value.includes(opt) ? value.filter((v) => v !== opt) : [...value, opt]);
  };
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.length === 0 ? (
        <span className="text-xs text-muted-foreground">{placeholder}</span>
      ) : null}
      {options.map((opt) => {
        const active = value.includes(opt);
        return (
          <button
            key={opt}
            type="button"
            onClick={() => toggle(opt)}
            className={cn(
              "rounded-full border px-2.5 py-1 text-xs transition-colors",
              active
                ? "border-primary bg-primary/15 text-primary"
                : "border-border bg-background text-muted-foreground hover:bg-secondary/60",
            )}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}

function TagInput({
  value,
  onChange,
  placeholder,
}: {
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
}) {
  const [draft, setDraft] = useState("");
  const add = () => {
    const t = draft.trim();
    if (!t || value.includes(t)) return;
    onChange([...value, t]);
    setDraft("");
  };
  return (
    <div className="rounded-lg border border-input bg-background p-2">
      <div className="flex flex-wrap gap-1.5">
        {value.map((tag) => (
          <span
            key={tag}
            className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary"
          >
            {tag}
            <button
              type="button"
              onClick={() => onChange(value.filter((v) => v !== tag))}
              className="hover:text-primary/70"
              aria-label={`Remove ${tag}`}
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === ",") {
              e.preventDefault();
              add();
            } else if (e.key === "Backspace" && !draft && value.length > 0) {
              onChange(value.slice(0, -1));
            }
          }}
          onBlur={add}
          placeholder={value.length === 0 ? placeholder : ""}
          className="min-w-[120px] flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
        />
      </div>
    </div>
  );
}

function TierSection({
  index,
  title,
  subtitle,
  done,
  total,
  defaultOpen,
  children,
}: {
  index: 1 | 2 | 3;
  title: string;
  subtitle: string;
  done: number;
  total: number;
  defaultOpen: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const complete = done === total;
  return (
    <Collapsible open={open} onOpenChange={setOpen} className="rounded-2xl border border-border bg-card">
      <CollapsibleTrigger className="flex w-full items-center justify-between gap-3 p-4 text-left">
        <div className="flex items-center gap-3 min-w-0">
          <span
            className={cn(
              "flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold",
              complete ? "bg-primary text-primary-foreground" : "bg-secondary text-foreground",
            )}
          >
            {index}
          </span>
          <div className="min-w-0">
            <p className="text-sm font-semibold">{title}</p>
            <p className="truncate text-xs text-muted-foreground">{subtitle}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={cn("text-xs", complete ? "text-primary" : "text-muted-foreground")}>
            {done}/{total}
          </span>
          <ChevronDown className={cn("h-4 w-4 text-muted-foreground transition-transform", open && "rotate-180")} />
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent className="space-y-4 border-t border-border p-4">
        {children}
      </CollapsibleContent>
    </Collapsible>
  );
}

const SettingsTab = ({ userId, email, onProfileUpdated }: SettingsTabProps) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const [displayName, setDisplayName] = useState("");
  const [bio, setBio] = useState("");
  const [organization, setOrganization] = useState("");
  const [role, setRole] = useState<string>("");
  const [location, setLocation] = useState("");
  const [avatarUrl, setAvatarUrl] = useState<string>("");

  // Tier 1 (essentials)
  const [stakeholderType, setStakeholderType] = useState<string>("");
  // Tier 2 (completion)
  const [headline, setHeadline] = useState("");
  const [focusAreas, setFocusAreas] = useState<string[]>([]);
  const [countiesServed, setCountiesServed] = useState<string[]>([]);
  // Tier 3 (power user)
  const [skills, setSkills] = useState<string[]>([]);
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [linkedinUrl, setLinkedinUrl] = useState("");
  const [foundedYear, setFoundedYear] = useState<string>("");
  const [certifications, setCertifications] = useState("");
  const [lookingFor, setLookingFor] = useState<string[]>([]);

  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from("profiles")
        .select("*")
        .eq("user_id", userId)
        .maybeSingle();
      if (data) {
        setDisplayName(data.display_name ?? "");
        setBio((data as any).bio ?? "");
        setOrganization((data as any).organization ?? "");
        setRole((data as any).role ?? "");
        setLocation((data as any).location ?? "");
        setAvatarUrl(data.avatar_url ?? "");
        const d = data as any;
        setStakeholderType(d.stakeholder_type ?? "");
        setHeadline(d.headline ?? "");
        setFocusAreas(d.focus_areas ?? []);
        setCountiesServed(d.counties_served ?? []);
        setSkills(d.skills ?? []);
        setWebsiteUrl(d.website_url ?? "");
        setLinkedinUrl(d.linkedin_url ?? "");
        setFoundedYear(d.founded_year ? String(d.founded_year) : "");
        setCertifications(d.certifications ?? "");
        setLookingFor(d.looking_for ?? []);
      }
      setLoading(false);
    })();
  }, [userId]);

  const handleSave = async () => {
    setSaving(true);
    const yearNum = parseInt(foundedYear, 10);
    const payload: Record<string, unknown> = {
      user_id: userId,
      display_name: displayName.trim() || null,
      bio: bio.trim() || null,
      organization: organization.trim() || null,
      role: role || null,
      location: location.trim() || null,
      avatar_url: avatarUrl || null,
      stakeholder_type: stakeholderType || null,
      headline: headline.trim() || null,
      focus_areas: focusAreas,
      counties_served: countiesServed,
      skills: skills,
      website_url: websiteUrl.trim() || null,
      linkedin_url: linkedinUrl.trim() || null,
      founded_year: Number.isFinite(yearNum) && yearNum > 0 ? yearNum : null,
      certifications: certifications.trim() || null,
      looking_for: lookingFor,
    };
    // Single upsert keyed on user_id — atomic, no race, no missing-row branch.
    const { error } = await supabase
      .from("profiles")
      .upsert(payload as any, { onConflict: "user_id" });
    setSaving(false);
    if (error) {
      console.error("[profile save] upsert failed", error);
      toast.error(error.message || "Could not save profile");
      return;
    }
    toast.success("Profile updated");
    onProfileUpdated?.();
  };

  const handleAvatarUpload = async (file: File) => {
    setUploading(true);
    const ext = (file.name.split(".").pop() || "png").toLowerCase();
    // Stable filename per extension — true upsert, prevents storage bloat.
    const path = `${userId}/avatar.${ext}`;

    // Clean up any prior avatars with a different extension so we don't accumulate files.
    const { data: existing } = await supabase.storage.from("avatars").list(userId);
    const stale = (existing ?? [])
      .filter((f) => f.name.startsWith("avatar.") && f.name !== `avatar.${ext}`)
      .map((f) => `${userId}/${f.name}`);
    if (stale.length > 0) {
      await supabase.storage.from("avatars").remove(stale);
    }

    const { error: uploadErr } = await supabase.storage
      .from("avatars")
      .upload(path, file, { upsert: true, contentType: file.type });
    if (uploadErr) {
      setUploading(false);
      console.error("[avatar upload] failed", uploadErr);
      toast.error(uploadErr.message || "Upload failed");
      return;
    }
    const { data: pub } = supabase.storage.from("avatars").getPublicUrl(path);
    // Cache-bust so the new image renders immediately
    const bustedUrl = `${pub.publicUrl}?v=${Date.now()}`;
    setAvatarUrl(bustedUrl);
    // Persist immediately so the header reflects the change
    const { error: persistErr } = await supabase
      .from("profiles")
      .upsert(
        { user_id: userId, avatar_url: bustedUrl },
        { onConflict: "user_id" },
      );
    setUploading(false);
    if (persistErr) {
      console.error("[avatar persist] failed", persistErr);
      toast.error(persistErr.message || "Could not save avatar");
      return;
    }
    toast.success("Avatar updated");
    onProfileUpdated?.();
  };

  const initials = (displayName || email || "?")
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase())
    .join("");

  const currentProfile: ProfileRow = {
    display_name: displayName,
    avatar_url: avatarUrl,
    location,
    stakeholder_type: stakeholderType,
    headline,
    bio,
    organization,
    focus_areas: focusAreas,
    counties_served: countiesServed,
    skills,
    website_url: websiteUrl,
    linkedin_url: linkedinUrl,
    founded_year: foundedYear ? parseInt(foundedYear, 10) : null,
    certifications,
    looking_for: lookingFor,
  };
  const { tier, percent, tierStatus } = useProfileCompletion(currentProfile);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 rounded-2xl border border-border bg-card p-4">
        <div>
          <p className="text-sm font-semibold">Profile strength</p>
          <p className="text-xs text-muted-foreground">{percent}% complete</p>
        </div>
        <CompletionBadge tier={tier} />
      </div>

      <div className="flex items-center gap-4 rounded-2xl border border-border bg-card p-6">
        <Avatar className="h-16 w-16 border border-border">
          {avatarUrl ? <AvatarImage src={avatarUrl} alt="Avatar" /> : null}
          <AvatarFallback className="bg-muted text-base font-semibold text-foreground">
            {initials || "U"}
          </AvatarFallback>
        </Avatar>
        <div className="space-y-2">
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleAvatarUpload(f);
              e.target.value = "";
            }}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
          >
            {uploading ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Upload className="mr-2 h-4 w-4" />
            )}
            {uploading ? "Uploading..." : "Change avatar"}
          </Button>
          <p className="text-xs text-muted-foreground">PNG or JPG, up to a few MB.</p>
        </div>
      </div>

      <TierSection
        index={1}
        title="Essentials"
        subtitle="What other shareholders need to recognize you."
        done={tierStatus[0].done.length}
        total={tierStatus[0].spec.length}
        defaultOpen={!tierStatus[0].complete}
      >
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="displayName">Display name</Label>
            <Input
              id="displayName"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Your name"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="location">Home county</Label>
            <Select value={location} onValueChange={setLocation}>
              <SelectTrigger id="location">
                <SelectValue placeholder="Select a county" />
              </SelectTrigger>
              <SelectContent>
                {ALABAMA_COUNTIES.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5 md:col-span-2">
            <Label>Stakeholder type</Label>
            <ChipMulti
              options={STAKEHOLDER_TYPES}
              value={stakeholderType ? [stakeholderType] : []}
              onChange={(next) => setStakeholderType(next[next.length - 1] ?? "")}
            />
          </div>
        </div>
      </TierSection>

      <TierSection
        index={2}
        title="Profile completion"
        subtitle="Credibility, focus, and where you serve."
        done={tierStatus[1].done.length}
        total={tierStatus[1].spec.length}
        defaultOpen={tierStatus[0].complete && !tierStatus[1].complete}
      >
        <div className="space-y-1.5">
          <Label htmlFor="headline">Headline</Label>
          <Input
            id="headline"
            value={headline}
            onChange={(e) => setHeadline(e.target.value)}
            placeholder="e.g. DBE-certified architect serving the Gulf Coast"
            maxLength={120}
          />
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="organization">Organization</Label>
            <Input
              id="organization"
              value={organization}
              onChange={(e) => setOrganization(e.target.value)}
              placeholder="e.g. Mobile Community Coalition"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="role">Audience role (legacy)</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger id="role">
                <SelectValue placeholder="Select a role" />
              </SelectTrigger>
              <SelectContent>
                {ROLE_OPTIONS.map((r) => (
                  <SelectItem key={r} value={r}>
                    {r}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="bio">Bio</Label>
          <Textarea
            id="bio"
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            placeholder="Tell the community about your work (120+ characters)..."
            rows={4}
          />
          <p className="text-[11px] text-muted-foreground">{bio.trim().length}/120 minimum</p>
        </div>
        <div className="space-y-1.5">
          <Label>Focus areas</Label>
          <ChipMulti options={FOCUS_AREAS} value={focusAreas} onChange={setFocusAreas} />
        </div>
        <div className="space-y-1.5">
          <Label>Counties served</Label>
          <ChipMulti
            options={ALABAMA_COUNTIES}
            value={countiesServed}
            onChange={setCountiesServed}
          />
        </div>
      </TierSection>

      <TierSection
        index={3}
        title="Power user"
        subtitle="Unlock better matching and trust signals."
        done={tierStatus[2].done.length}
        total={tierStatus[2].spec.length}
        defaultOpen={tierStatus[0].complete && tierStatus[1].complete && !tierStatus[2].complete}
      >
        <div className="space-y-1.5">
          <Label>Skills / services offered</Label>
          <TagInput value={skills} onChange={setSkills} placeholder="Type a skill and press Enter" />
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="websiteUrl">Website</Label>
            <Input
              id="websiteUrl"
              value={websiteUrl}
              onChange={(e) => setWebsiteUrl(e.target.value)}
              placeholder="https://"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="linkedinUrl">LinkedIn</Label>
            <Input
              id="linkedinUrl"
              value={linkedinUrl}
              onChange={(e) => setLinkedinUrl(e.target.value)}
              placeholder="https://linkedin.com/in/…"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="foundedYear">Founded year</Label>
            <Input
              id="foundedYear"
              value={foundedYear}
              onChange={(e) => setFoundedYear(e.target.value.replace(/\D/g, "").slice(0, 4))}
              placeholder="e.g. 2018"
              inputMode="numeric"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="certifications">Certifications</Label>
            <Input
              id="certifications"
              value={certifications}
              onChange={(e) => setCertifications(e.target.value)}
              placeholder="e.g. DBE, SAM.gov registered"
            />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label>What you're looking for</Label>
          <ChipMulti options={LOOKING_FOR} value={lookingFor} onChange={setLookingFor} />
        </div>
      </TierSection>

      <div className="sticky bottom-4 z-10 flex justify-end">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-primary text-primary-foreground shadow-lg hover:bg-primary/90"
        >
          {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Save changes
        </Button>
      </div>
    </div>
  );
};

export default SettingsTab;
